import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import authService from './auth'
import { transformMessageData } from './messageData'
// Type-only: erased at compile time, so this does NOT create a runtime cycle
// with the auth store (which imports this module).
import type {
  ClientSubscription,
  PendingSubscriptionChange,
  SubscriptionPackage
} from '@/stores/auth'
import router from '@/router'
import { config } from '@/config'

interface ApiError {
  error: string
  message?: string
  details?: any
  /**
   * Stable machine-readable code (e.g. `same_package`, `tier_in_use`). Unlike
   * `error`, which is an English sentence, this is safe to translate.
   */
  code?: string
}

/**
 * The filename a `Content-Disposition` header asks for, or `''`.
 *
 * `filename*` (RFC 5987) wins over the plain `filename` when both are present,
 * because that is the one that survives non-ASCII. Anything that looks like a
 * path is rejected outright — a download name is a leaf, never a location.
 */
function filenameFromContentDisposition(header: unknown): string {
  if (typeof header !== 'string' || !header) return ''

  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header)
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header)

  let name = ''
  if (extended) {
    try {
      name = decodeURIComponent(extended[1].trim())
    } catch {
      name = ''
    }
  }
  if (!name && plain) name = (plain[2] ?? plain[1] ?? '').trim()

  name = name.replace(/^["']|["']$/g, '').trim()
  if (!name || name.includes('/') || name.includes('\\') || name === '.' || name === '..') return ''
  return name
}

/** One line of a worked calculation (see backend services/formula_registry.py). */
export interface ExplanationStep {
  /** Metric key, account code / prefix, or period label — depends on `kind`. */
  label: string
  kind: 'ref' | 'accounts' | 'period' | 'literal'
  sign: 1 | -1
  /** Decimal STRING, like every other amount in this API. */
  value: string
  accounts: Array<{ code: string; name: string; column: string; value: string }>
}

/** How one statistics figure was calculated, for the explanation panel. */
export interface MetricExplanation {
  metric: string
  scope: 'month' | 'total' | 'ratio' | 'meta'
  kind: 'money' | 'ratio' | 'percent' | 'days' | 'count' | 'flag' | 'list'
  places: string
  formula: string
  /** i18n keys under `statistics.explain.notes`, never prose. */
  notes: string[]
  steps: ExplanationStep[]
  /** The figure the page is showing; null when it is not stored as a scalar. */
  value: string | null
  /** The same figure re-derived here from the stored rows. */
  recomputed: string | null
  /** null when there is nothing to compare — the UI then stays quiet. */
  matches_stored: boolean | null
  current_formula_version: number
  formula_version?: number
  period?: string
  available_periods?: string[]
  range?: {
    first: string | null
    last: string | null
    months: number
    days: string | null
    missing_periods: string[]
  }
  source_metric?: string | null
  mode?: string
  undefined_reason?: string | null
  parts?: {
    numerator: { value: string; steps: ExplanationStep[] }
    denominator: { value: string; steps: ExplanationStep[] } | null
    multiplier: ExplanationStep | null
  }
}

/** One gateable capability, as the admin package editor lists them. */
export interface SubscriptionFeatureKey {
  key: string
  /** Grouping for the checkbox list ('stats', 'invoicing', …). */
  group: string
  /** True when the key is part of the Basic package (informational). */
  basic: boolean
}

/** A row of the per-client admin list. The firm arrives flattened here. */
export interface SubscriptionClientRow {
  id: string
  societate: string | null
  cui: string | null
  firm_id: string | null
  firm_name: string | null
  subscription: ClientSubscription
}

export interface SubscriptionClientsResponse {
  clients: SubscriptionClientRow[]
  total: number
  page: number
  per_page: number
  pages: number
  has_more?: boolean
}

/** One line of the monthly report: what this client is billed for that month. */
export interface SubscriptionReportRow {
  client: { id: string; societate: string | null; cui: string | null }
  firm: { id: string; name: string } | null
  package: SubscriptionPackage
  /** Where the period came from: admin | self | migration | system. */
  source: string
}

export interface SubscriptionReport {
  year: number
  month: number
  range?: { from: string; to: string }
  firm_id: string | null
  package_id?: string | null
  rows: SubscriptionReportRow[]
  summary: {
    packages: Array<{ package: SubscriptionPackage; count: number; revenue_ron: number }>
    total_clients: number
    total_revenue_ron: number
  }
}

/** An audit row from the per-client change log. */
export interface SubscriptionHistoryEvent {
  id?: string
  event_type: string
  occurred_at: string
  actor_id: string | null
  actor_name?: string | null
  data: Record<string, any> | null
}

export interface InvoiceSettings {
  id?: string
  client_id: string
  series_code: string
  next_number: number
  default_due_days: number
  country_code: string
  county: string | null
  city: string | null
  address: string | null
  postal_code: string | null
  iban: string | null
  bank_name: string | null
  is_complete: boolean
  has_invoices?: boolean
  seller?: {
    company_name: string
    cui: string
    registration_number?: string | null
    vat_payer: boolean
    email?: string | null
    phone?: string | null
  }
}

export interface InvoiceCustomer {
  id: string
  company_name: string
  cui: string
  registration_number?: string | null
  vat_payer: boolean
  country_code: string
  county: string
  city: string
  address: string
  postal_code?: string | null
  email?: string | null
  phone?: string | null
  client_code?: string | null
  iban?: string | null
  bank_name?: string | null
  contact_person?: string | null
  default_due_days: number
}

export interface InvoiceLine {
  id: string
  description: string
  unit_code: string
  unit_label: string
  quantity: string
  unit_price: string
  vat_rate: string
  subtotal: string
  vat_amount: string
  total: string
}

export interface IssuedInvoice {
  id: string
  customer_id: string
  status: string
  series: string
  number: number
  display_number: string
  issue_date: string
  due_date: string
  currency: string
  subtotal: string
  vat_total: string
  total: string
  notes?: string | null
  prepared_by?: string | null
  delegate_name?: string | null
  delegate_id?: string | null
  vehicle_number?: string | null
  order_number?: string | null
  contract_number?: string | null
  project_reference?: string | null
  buyer_reference?: string | null
  delivery_date?: string | null
  internal_note?: string | null
  seller: Record<string, any>
  buyer: Record<string, any>
  lines: InvoiceLine[]
  spv_status: 'manual_required' | 'submitted_manual_unverified'
  spv_submitted_at?: string | null
  spv_upload_id?: string | null
}

export interface ConnectorSyncStats {
  lists_total: number
  lists_done: number
  lists_failed: number
  downloads_enqueued: number
  downloads_done: number
  downloads_failed: number
  documents_stored: number
  skipped_existing: number
}

export interface ConnectorSyncRun {
  id: string
  created_at: string
  updated_at: string
  status: 'running' | 'completed' | 'failed'
  trigger: 'manual' | 'cron'
  started_by: string | null
  started_at: string | null
  finished_at: string | null
  // A run that has not reached a phase yet simply omits that counter.
  stats: Partial<ConnectorSyncStats>
  error: string | null
}

export interface ConnectorStatus {
  last_seen_at: string | null
  online: boolean
  connector_version: string | null
  hostname: string | null
  tdec_ok: boolean | null
  pending_commands: number
  dispatched_commands: number
  poll_interval: number
  sync: {
    active_run: ConnectorSyncRun | null
    last_run: ConnectorSyncRun | null
  }
}

export type ConnectorAction = 'list' | 'companies' | 'download'

/**
 * Turns a `download` into an ARCHIVE: the result is filed server-side into the
 * matched client's folder instead of being handed back to the browser. The CUI
 * must already belong to a client of the caller's firm — otherwise the enqueue
 * itself is refused (400) rather than the document being filed nowhere.
 */
export interface ConnectorSaveMeta {
  cif: string
  tip?: string
  doc_date?: string
  period?: string
}

export interface ConnectorCommand {
  id: string
  created_at: string
  updated_at: string
  action: ConnectorAction
  status: 'pending' | 'dispatched' | 'ok' | 'error'
  params: Record<string, any>
  error: string | null
  created_by: string | null
  dispatched_at: string | null
  completed_at: string | null
  duration_ms: number | null
  payload_size: number
  // Archiving intent and outcome. `store_client_id` set means the result is
  // filed server-side; `stored_file_id` appears once it has been.
  store_client_id: string | null
  stored_file_id: string | null
  sync_run_id: string | null
  // Only present on GET /connector/commands/:id (single-command endpoint)
  payload?: any
}

/** One TDec document already archived into the platform (the dedup ledger). */
export interface ConnectorDocument {
  id: string
  created_at: string
  updated_at: string
  uid: string
  category: string
  format: string
  cif: string
  client_id: string
  client_name: string | null
  file_id: string
  file_name: string | null
  // The ledger row outlives the file: "archived, then deleted" is a real state.
  file_deleted: boolean
  tip: string | null
  doc_date: string | null
  period: string | null
  source: 'manual' | 'sync'
  created_by: string | null
}

// ---- Company profile (ANAF / ONRC) and filed annual financials ----

export interface CompanyProfileAddress {
  full: string | null
  street: string | null
  number: string | null
  locality: string | null
  county: string | null
  county_code: string | null
  postal_code: string | null
  details: string | null
}

export interface CompanyProfileVatPeriod {
  start: string
  end: string | null
  message: string | null
}

export interface CompanyProfileVat {
  registered: boolean | null
  periods: CompanyProfileVatPeriod[]
  cash_basis: boolean | null
  cash_basis_start: string | null
  cash_basis_end: string | null
  split_vat: boolean | null
}

export interface CompanyProfileAdministrator {
  name: string
  role: string | null
}

export interface CompanyProfileTimelineEvent {
  date: string | null
  type: string | null
  source: string | null
  summary: string
}

export interface CompanyProfile {
  cui: string
  /** The client's CUI changed since this profile was fetched. */
  stale: boolean
  name: string | null
  registration_number: string | null
  registration_date: string | null
  registration_state: string | null
  legal_form: string | null
  organization_form: string | null
  ownership_form: string | null
  fiscal_authority: string | null
  main_caen: string | null
  authorized_caen: string[]
  phone: string | null
  fax: string | null
  iban: string | null
  address: CompanyProfileAddress
  vat: CompanyProfileVat
  efactura_registered: boolean | null
  fiscally_inactive: boolean | null
  inactive_since: string | null
  reactivated_since: string | null
  deregistered_at: string | null
  /** e.g. "Funcțiune" | "Dizolvare" — anything else than "Funcțiune" is bad news. */
  onrc_status: string | null
  administrators: CompanyProfileAdministrator[]
  timeline: CompanyProfileTimelineEvent[]
  seap: { contracts: number | null; direct_purchases: number | null }
  anaf_fetched_at: string | null
  anaf_last_error: null | 'not_found' | string
  registry_source: string | null
  registry_fetched_at: string | null
}

export interface CompanyProfileResponse {
  client_id: string
  profile: CompanyProfile
}

/** One filed year. Every amount is a whole number of RON (or null). */
export interface AnnualFinancialsYear {
  year: number
  company_name: string | null
  caen_code: string | null
  caen_description: string | null
  eur_rate: string | null
  net_result: number | null
  source: string | null
  fetched_at: string | null
  fixed_assets: number | null
  current_assets: number | null
  inventories: number | null
  receivables: number | null
  cash: number | null
  prepaid_expenses: number | null
  total_liabilities: number | null
  deferred_revenue: number | null
  provisions: number | null
  total_equity: number | null
  paid_in_capital: number | null
  state_equity: number | null
  net_turnover: number | null
  total_revenue: number | null
  total_expenses: number | null
  gross_profit: number | null
  gross_loss: number | null
  net_profit: number | null
  net_loss: number | null
  avg_employees: number | null
}

export interface AnnualIndicator {
  key: string
  code: string
  label: string
}

export interface AnnualProfileSummary {
  name: string | null
  onrc_status: string | null
  fiscally_inactive: boolean | null
  vat_registered: boolean | null
  main_caen: string | null
  stale: boolean
}

export interface AnnualStatisticsResponse {
  client_id: string
  cui: string
  /** Newest first. */
  years: AnnualFinancialsYear[]
  indicators: AnnualIndicator[]
  profile_summary: AnnualProfileSummary | null
}

class ApiService {
  private api: AxiosInstance
  private socketApi: AxiosInstance


  constructor() {
    this.api = axios.create({
      baseURL: config.api.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.socketApi = axios.create({
      baseURL: config.api.wsURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    const requestInterceptor = (config: InternalAxiosRequestConfig) => {
      const token = authService.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    }

    /**
     * Carry the server's structured error forward onto the thrown Error.
     *
     * Two things travel: the whole `.response` (so consumers reading
     * `err.response?.data?.…` see the payload instead of silently falling back
     * to a generic message) and the stable machine-readable `code` when the
     * body has one. The code is the only part of an error body that is safe to
     * translate — `error` is an English sentence written for a developer,
     * `code` is a contract (see `subscriptionErrorMessage`). Flattening a
     * rejection to `new Error(data.error)` used to drop both.
     *
     * The message each branch chooses is deliberately left untouched.
     */
    const withServerError = (err: Error, error: AxiosError<ApiError>): Error => {
      if (error.response) {
        ;(err as any).response = error.response
        const code = (error.response.data as ApiError | undefined)?.code
        if (typeof code === 'string' && code) (err as any).code = code
      }
      return err
    }

    const responseErrorInterceptor = async (error: AxiosError<ApiError>) => {
      // Only handle auth errors when there's an actual response from the server
      // Network errors (server down) should not trigger logout
      if (error.response?.status === 401) {
        authService.removeToken()
        await router.push({ name: 'login' })
        return Promise.reject(withServerError(new Error('Authentication required'), error))
      }

      // A 403 usually carries a specific reason from the server (e.g. "Statistics
      // are not enabled for this client"). Flattening it to a bare "Access denied"
      // threw that away and, because the thrown Error had no `.response`, every
      // consumer reading `err.response.data.error` silently fell back to a generic
      // message. Keep the server's wording AND re-attach the response.
      if (error.response?.status === 403) {
        const denied = new Error(error.response?.data?.error || 'Access denied')
        return Promise.reject(withServerError(denied, error))
      }

      if (error.response?.status === 429) {
        return Promise.reject(
          withServerError(new Error('Too many requests. Please try again later.'), error)
        )
      }

      // Network error (server down) - don't clear auth, preserve the error
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return Promise.reject(error)
      }

      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'An error occurred'
      return Promise.reject(withServerError(new Error(message), error))
    }

    this.api.interceptors.request.use(requestInterceptor)
    this.api.interceptors.response.use((response) => response, responseErrorInterceptor)

    this.socketApi.interceptors.request.use(requestInterceptor)
    this.socketApi.interceptors.response.use((response) => response, responseErrorInterceptor)
  }

  get client(): AxiosInstance {
    return this.api
  }

  get socketClient(): AxiosInstance {
    return this.socketApi
  }

  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password })
    return response.data
  }

  async logout() {
    try {
      await this.api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  }

  async getMe() {
    const response = await this.api.get('/users/me')
    return response.data.user
  }

  async getActivityStats() {
    const response = await this.api.get('/users/activity-stats')
    return response.data
  }

  async getInvoiceSettings() {
    const response = await this.api.get('/invoice-settings')
    return response.data.settings as InvoiceSettings
  }

  async updateInvoiceSettings(data: Partial<InvoiceSettings>) {
    const response = await this.api.put('/invoice-settings', data)
    return response.data.settings as InvoiceSettings
  }

  async getInvoiceCustomers(search?: string) {
    const response = await this.api.get('/invoice-customers', {
      params: search ? { search } : undefined
    })
    return response.data.customers as InvoiceCustomer[]
  }

  async createInvoiceCustomer(data: Omit<InvoiceCustomer, 'id'>) {
    const response = await this.api.post('/invoice-customers', data)
    return response.data.customer as InvoiceCustomer
  }

  async updateInvoiceCustomer(id: string, data: Partial<InvoiceCustomer>) {
    const response = await this.api.patch(`/invoice-customers/${id}`, data)
    return response.data.customer as InvoiceCustomer
  }

  async getInvoices(params?: { page?: number; per_page?: number }) {
    const response = await this.api.get('/invoices', { params })
    return response.data as {
      invoices: IssuedInvoice[]
      page: number
      pages: number
      total: number
      has_more: boolean
    }
  }

  async getInvoice(id: string) {
    const response = await this.api.get(`/invoices/${id}`)
    return response.data.invoice as IssuedInvoice
  }

  async createInvoice(data: {
    customer_id: string
    issue_date: string
    due_date: string
    notes?: string
    prepared_by?: string
    delegate_name?: string
    delegate_id?: string
    vehicle_number?: string
    order_number?: string
    contract_number?: string
    project_reference?: string
    buyer_reference?: string
    delivery_date?: string
    internal_note?: string
    idempotency_key: string
    lines: Array<{
      description: string
      unit_code: string
      quantity: string
      unit_price: string
      vat_rate: string
    }>
  }) {
    const response = await this.api.post('/invoices', data)
    return response.data as { invoice: IssuedInvoice; created: boolean }
  }

  async downloadInvoiceDocument(id: string, kind: 'pdf' | 'xml', invoiceNumber: string) {
    const response = await this.api.get(`/invoices/${id}/${kind}`, { responseType: 'blob' })
    const blob = new Blob([response.data], {
      type: kind === 'pdf' ? 'application/pdf' : 'application/xml'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${kind === 'pdf' ? 'factura' : 'efactura'}-${invoiceNumber}.${kind}`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  async markInvoiceSpvSubmitted(id: string, uploadId?: string) {
    const response = await this.api.post(`/invoices/${id}/mark-spv-submitted`, {
      upload_id: uploadId || undefined
    })
    return response.data.invoice as IssuedInvoice
  }

  async getConversations() {
    const response = await this.api.get('/conversations')
    return response.data
  }

  async getConversation(id: string) {
    const response = await this.api.get(`/conversations/${id}`)
    return response.data
  }

  async getOrCreateDepartmentConversation(departmentId: string | null, clientId?: string) {
    const response = await this.api.post('/conversations/for-department', {
      department_id: departmentId,
      client_id: clientId
    })
    return response.data
  }

  async getDepartments(firmId?: string) {
    const response = await this.api.get('/departments', {
      params: firmId ? { firm_id: firmId } : undefined
    })
    return response.data
  }

  async createDepartment(name: string, firmId?: string) {
    const response = await this.api.post('/departments', {
      name,
      firm_id: firmId
    })
    return response.data
  }

  async updateDepartment(departmentId: string, data: { name?: string; sort_order?: number }) {
    const response = await this.api.patch(`/departments/${departmentId}`, data)
    return response.data
  }

  // Per-client, per-department chat notification email routing (accountant/admin)
  async getClientMessageRoutes(clientId: string) {
    const response = await this.api.get(`/clients/${clientId}/message-routes`)
    return response.data
  }

  async setClientMessageRoutes(
    clientId: string,
    data: { department_id: string | null; emails: string[] }
  ) {
    const response = await this.api.put(`/clients/${clientId}/message-routes`, data)
    return response.data
  }

  async getMessages(
    conversationId: string,
    params?: { before?: string; before_sequence?: number; after?: string; after_sequence?: number; limit?: number; q?: string }
  ) {
    const response = await this.api.get(`/conversations/${conversationId}/messages`, { params })
    const responseData = response.data

    // Transform all messages in the response
    if (responseData.messages && Array.isArray(responseData.messages)) {
      responseData.messages = responseData.messages.map((message: any) =>
        transformMessageData(message)
      )
    }

    return responseData
  }

  private changeValidatorToken: string | null = null
  private changeValidators = new Map<string, { after: number; limit: number; etag: string }>()

  async getMessageChanges(conversationId: string, after: number, limit: number = 100) {
    const token = authService.getToken()
    if (this.changeValidatorToken !== token) {
      this.changeValidators.clear()
      this.changeValidatorToken = token
    }
    const cached = this.changeValidators.get(conversationId)
    const etag = cached?.after === after && cached.limit === limit ? cached.etag : undefined
    const response = await this.api.get(`/conversations/${conversationId}/changes`, {
      params: { after, limit },
      headers: etag ? { 'If-None-Match': etag } : undefined,
      validateStatus: status => (status >= 200 && status < 300) || status === 304
    })
    if (response.status === 304) {
      if (!etag) throw new Error('Unexpected unvalidated message response')
      return { messages: [], read_receipts: [], cursor: after, has_more: false, not_modified: true }
    }
    const nextEtag = response.headers?.etag
    if (typeof nextEtag === 'string' && response.data.cursor === after && response.data.has_more === false) {
      this.changeValidators.set(conversationId, { after, limit, etag: nextEtag })
      // Only validators are cached, never message contents. Bound long sessions.
      if (this.changeValidators.size > 128) this.changeValidators.delete(this.changeValidators.keys().next().value!)
    } else {
      this.changeValidators.delete(conversationId)
    }
    return {
      ...response.data,
      messages: (response.data.messages || []).map((message: any) => transformMessageData(message))
    }
  }

  async sendMessage(
    conversationId: string,
    text: string,
    attachmentTokens?: string[],
    quoteMessageId?: string,
    clientMessageId?: string
  ) {
    const response = await this.api.post(`/conversations/${conversationId}/messages`, {
      text,
      attachment_tokens: attachmentTokens,
      quote_message_id: quoteMessageId,
      client_message_id: clientMessageId
    })

    // The API returns { message: '...', data: { ... } } or { message: '...', data: [...] } for multiple files
    // We need to extract and transform the message data
    const messageData = response.data.data

    // Handle both single message and array of messages (for multiple file uploads)
    if (Array.isArray(messageData)) {
      return messageData.map(msg => transformMessageData(msg))
    } else {
      return transformMessageData(messageData)
    }
  }

  async editMessage(messageId: string, text: string) {
    const response = await this.api.put(`/messages/${messageId}`, { text })
    return response.data.data // Extract the actual message data from the API response
  }

  async deleteMessage(messageId: string) {
    const response = await this.api.delete(`/messages/${messageId}`)
    return response.data
  }

  async getMessageEdits(messageId: string) {
    const response = await this.api.get(`/messages/${messageId}/edits`)
    return response.data
  }

  async markConversationRead(conversationId: string, messageId: string) {
    const response = await this.api.post('/reads', {
      conversation_id: conversationId,
      message_id: messageId
    })
    return response.data
  }

  async getFiles(params?: {
    page?: number
    pageSize?: number
    q?: string
    folderId?: string
    clientId?: string
  }) {
    const response = await this.api.get('/files', { params })
    return response.data
  }

  async getFile(fileId: string) {
    const response = await this.api.get(`/files/${fileId}`)
    return response.data
  }

  async getFileNotes(fileId: string) {
    const response = await this.api.get(`/files/${fileId}/notes`)
    return response.data
  }

  async addFileNote(fileId: string, text: string) {
    const response = await this.api.post(`/files/${fileId}/notes`, { text })
    return response.data
  }

  async getBulkFileNotes(fileIds: string[]) {
    const response = await this.api.post('/files/notes/bulk', { file_ids: fileIds })
    return response.data
  }

  async getFolders(params?: { parentFolderId?: string; clientId?: string }) {
    const response = await this.api.get('/folders', { params })
    return response.data
  }

  async getFolderTree(clientId?: string) {
    const params: any = {}
    // Only add client_id if it's provided and not undefined
    if (clientId) {
      params.client_id = clientId
    }
    const response = await this.api.get('/folders/tree', { params })
    return response.data
  }

  async createFolder(name: string, parentFolderId?: string, clientId?: string) {
    const response = await this.api.post('/folders', {
      name,
      parent_folder_id: parentFolderId,
      client_id: clientId
    })
    return response.data
  }

  async createAccountingFolderStructure(clientId: string, parentFolderId?: string) {
    const response = await this.api.post('/folders/bulk-create-accounting-structure', {
      client_id: clientId,
      parent_folder_id: parentFolderId
    })
    return response.data
  }

  async renameFile(fileId: string, name: string) {
    const response = await this.api.patch(`/files/${fileId}`, { name })
    return response.data
  }

  async updateFileDescription(fileId: string, description: string) {
    const response = await this.api.patch(`/files/${fileId}/description`, { description })
    return response.data
  }

  async renameFolder(folderId: string, name: string) {
    const response = await this.api.put(`/folders/${folderId}`, { name })
    return response.data
  }

  async moveFiles(
    fileIds: string[],
    folderIds: string[],
    targetFolderId?: string,
    clientId?: string
  ) {
    const response = await this.api.post('/bulk/files/move', {
      file_ids: fileIds,
      folder_ids: folderIds,
      target_folder_id: targetFolderId,
      client_id: clientId
    })
    return response.data
  }

  async deleteFiles(fileIds: string[], folderIds: string[], clientId?: string) {
    const response = await this.api.post('/bulk/files/delete', {
      file_ids: fileIds,
      folder_ids: folderIds,
      client_id: clientId
    })
    return response.data
  }

  async getPresignedDownloadUrl(fileId: string, inline: boolean = false) {
    const params = inline ? { inline: 'true' } : {}
    const response = await this.api.get(`/files/${fileId}/download`, { params })
    return response.data
  }

  async downloadFiles(fileIds: string[], folderIds: string[]) {
    const response = await this.api.post('/bulk/files/download', {
      file_ids: fileIds,
      folder_ids: folderIds,
      include_folders: folderIds.length > 0
    })
    return response.data
  }

  async uploadFile(
    file: File,
    clientId?: string,
    folderId?: string,
    onProgress?: (progress: number) => void,
    fromChat?: boolean
  ) {
    const formData = new FormData()
    // Explicitly pass file.name to prevent browsers from sending
    // webkitRelativePath as the filename (which causes folder prefix in name)
    formData.append('file', file, file.name)
    if (clientId) {
      formData.append('client_id', clientId)
    }
    if (folderId) {
      formData.append('folder_id', folderId)
    }
    if (fromChat) {
      formData.append('from_chat', 'true')
    }

    const response = await this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 120000, // 2 minutes for file uploads
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })
    return response.data
  }

  // --- Direct S3 upload methods ---

  async requestUpload(params: {
    filename: string
    size: number
    mime_type: string
    client_id?: string
    folder_id?: string
    from_chat?: boolean
  }): Promise<{
    file_id: string
    filename: string
    presigned_url: string
    content_type: string
    expires_in: number
    was_renamed: boolean
  }> {
    const response = await this.api.post('/files/request-upload', params)
    return response.data
  }

  async confirmUpload(fileId: string): Promise<{ message: string; file: any }> {
    const response = await this.api.post(`/files/${fileId}/confirm-upload`)
    return response.data
  }

  async requestUploadBatch(params: {
    client_id: string
    files: Array<{
      filename: string
      size: number
      mime_type: string
      folder_id?: string
    }>
  }): Promise<{
    files: Array<{
      index: number
      file_id: string
      filename: string
      presigned_url: string
      content_type: string
      was_renamed: boolean
    }>
    errors: Array<{ index: number; filename: string; error: string }>
    expires_in: number
  }> {
    const response = await this.api.post('/files/request-upload-batch', params)
    return response.data
  }

  async confirmUploadBatch(
    fileIds: string[]
  ): Promise<{
    succeeded: Array<{ file_id: string; file: any }>
    failed: Array<{ file_id: string; error: string }>
  }> {
    const response = await this.api.post('/files/confirm-upload-batch', { file_ids: fileIds })
    return response.data
  }

  /**
   * Upload a file directly to S3 using a presigned PUT URL.
   * Uses raw XMLHttpRequest instead of Axios because:
   * - Axios would add the Authorization header, breaking the presigned URL signature
   * - XHR provides upload.onprogress for real progress tracking
   */
  uploadToS3(
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', presignedUrl)
      xhr.setRequestHeader('Content-Type', contentType)

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded * 100) / event.total)
          onProgress(progress)
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`))
        }
      }

      xhr.onerror = () => reject(new Error('S3 upload network error'))
      xhr.ontimeout = () => reject(new Error('S3 upload timed out'))
      xhr.timeout = 120000 // 2 minutes

      xhr.send(file)
    })
  }

  async getUnreadNotificationCount() {
    const response = await this.api.get('/notifications/unread/count')
    return response.data
  }

  async getNotifications(params?: { page?: number; pageSize?: number; unreadOnly?: boolean }) {
    const response = await this.api.get('/notifications', { params })
    return response.data
  }

  async markNotificationRead(notificationId: string) {
    const response = await this.api.patch(`/notifications/${notificationId}/read`)
    return response.data
  }

  async markAllNotificationsRead() {
    const response = await this.api.patch('/notifications/read-all')
    return response.data
  }

  async deleteNotification(notificationId: string) {
    const response = await this.api.delete(`/notifications/${notificationId}`)
    return response.data
  }

  async getNotificationPreferences() {
    const response = await this.api.get('/notifications/preferences')
    return response.data
  }

  async updateNotificationPreferences(preferences: any) {
    const response = await this.api.patch('/notifications/preferences', preferences)
    return response.data
  }

  // Push notification endpoints
  async getVapidPublicKey() {
    const response = await this.api.get('/push/vapid-public-key')
    return response.data.public_key
  }

  async subscribeToPush(subscription: PushSubscriptionJSON) {
    const response = await this.api.post('/push/subscribe', { subscription })
    return response.data
  }

  async unsubscribeFromPush(endpoint: string) {
    const response = await this.api.post('/push/unsubscribe', { endpoint })
    return response.data
  }

  async testPush() {
    const response = await this.api.post('/push/test')
    return response.data
  }

  // Deprecated: Use updateUser(userId, data) instead
  // async updateProfile(data: { name?: string; phone?: string }) {
  //   const response = await this.api.patch('/users/me', data)
  //   return response.data
  // }

  // Admin endpoints
  async getUsers(params?: {
    page?: number
    search?: string
    role?: string
    status?: string
    firm_id?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) {
    // Transform camelCase to snake_case for backend
    const apiParams: Record<string, any> = { ...params }
    if (params?.sortBy) {
      apiParams.sort_by = params.sortBy
      delete apiParams.sortBy
    }
    if (params?.sortOrder) {
      apiParams.sort_order = params.sortOrder
      delete apiParams.sortOrder
    }
    const response = await this.api.get('/users', { params: apiParams })
    return response.data
  }

  async getUser(userId: string) {
    const response = await this.api.get(`/users/${userId}`)
    return response.data.user
  }

  async createUser(data: any) {
    const response = await this.api.post('/users', data)
    return response.data
  }

  async updateUser(userId: string, data: any) {
    const response = await this.api.patch(`/users/${userId}`, data)
    return response.data
  }

  async deleteUser(userId: string) {
    const response = await this.api.delete(`/users/${userId}`)
    return response.data
  }

  async getUsersDropdown() {
    const response = await this.api.get('/users/dropdown')
    return response.data
  }

  // Accountant assignment endpoints
  async getUserAccountants(userId: string) {
    const response = await this.api.get(`/users/${userId}/accountants`)
    return response.data
  }

  async assignAccountantsToUser(userId: string, accountantIds: string[]) {
    const response = await this.api.post(`/users/${userId}/accountants`, {
      accountant_ids: accountantIds
    })
    return response.data
  }

  async unassignAccountantFromUser(userId: string, accountantId: string) {
    const response = await this.api.delete(`/users/${userId}/accountants/${accountantId}`)
    return response.data
  }

  async getAccountantUsers(accountantId: string) {
    const response = await this.api.get(`/users/accountants/${accountantId}/users`)
    return response.data
  }

  async getFirms(params?: { page?: number; search?: string }) {
    const response = await this.api.get('/firms', { params })
    return response.data
  }

  async getFirm(firmId: string) {
    const response = await this.api.get(`/firms/${firmId}`)
    return response.data
  }

  async createFirm(data: any) {
    const response = await this.api.post('/firms', data)
    return response.data
  }

  async updateFirm(firmId: string, data: any) {
    const response = await this.api.patch(`/firms/${firmId}`, data)
    return response.data
  }

  async deleteFirm(firmId: string) {
    const response = await this.api.delete(`/firms/${firmId}`)
    return response.data
  }

  async getClients(params?: {
    page?: number
    search?: string
    firmId?: string
    pageSize?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) {
    // Transform camelCase to snake_case for backend
    const apiParams: Record<string, any> = { ...params }
    if (params?.sortBy) {
      apiParams.sort_by = params.sortBy
      delete apiParams.sortBy
    }
    if (params?.sortOrder) {
      apiParams.sort_order = params.sortOrder
      delete apiParams.sortOrder
    }
    const response = await this.api.get('/clients', { params: apiParams })
    return response.data
  }

  async getClient(clientId: string) {
    const response = await this.api.get(`/clients/${clientId}`)
    return response.data
  }

  async createClient(data: any) {
    const response = await this.api.post('/clients', data)
    return response.data
  }

  // ---- Financial statistics (derived from imported trial balances) ----
  //
  // Amounts come back as decimal STRINGS, never numbers. These are accounting
  // figures that must reconcile to the cent, and JSON numbers are IEEE doubles
  // — parsing them would introduce drift. Format them as strings; only convert
  // for charting, where a pixel of error is irrelevant.

  async getStatisticsCompanies(clientId: string) {
    const response = await this.api.get(`/clients/${clientId}/statistics/companies`)
    return response.data as {
      client_id: string
      companies: Array<{
        cui: string
        company_name: string | null
        period_count: number
        first_period: string
        last_period: string
      }>
    }
  }

  async getStatisticsPeriods(clientId: string, cui?: string) {
    const response = await this.api.get(`/clients/${clientId}/statistics/periods`, {
      params: cui ? { cui } : {}
    })
    return response.data as {
      client_id: string
      periods: Array<{
        id: string
        cui: string
        company_name: string | null
        year: number
        month: number
        period: string
        status: string
        warnings: string[]
        source_file_id: string | null
        ingested_at: string | null
      }>
    }
  }

  async getStatistics(
    clientId: string,
    params?: { cui?: string; from?: string; to?: string }
  ) {
    const response = await this.api.get(`/clients/${clientId}/statistics`, { params })
    // `locked_sections` lists the feature keys this reader's package does not
    // include; the server has already stripped those figures from `stats`. It
    // is present on EVERY return path, including the empty ones, so the page
    // can distinguish "nothing imported" from "not included in your package".
    return response.data as {
      client_id?: string
      stats: any
      locked_sections?: string[]
    }
  }

  /**
   * How one figure on the statistics page was calculated.
   *
   * Errors are deliberately NOT flattened by the shared interceptor: a 422
   * carries a `reason` code ("we cannot show the working, because…") which is
   * a different state from a failure, and the panel renders its own wording
   * for each. Same shape as `importBalance()` — see services/CLAUDE.md.
   */
  async explainMetric(
    clientId: string,
    params: { metric: string; cui?: string; period?: string; from?: string; to?: string }
  ) {
    const response = await this.api.get(`/clients/${clientId}/statistics/explain`, {
      params,
      validateStatus: (status) => status !== 401
    })
    if (response.status >= 400) {
      const data = (response.data ?? {}) as { error?: string; reason?: string }
      const error = new Error(data.error || 'Explanation failed')
      ;(error as any).response = response
      throw error
    }
    return response.data as { client_id: string; cui: string; explanation: MetricExplanation }
  }

  async importBalance(fileId: string) {
    // The shared response interceptor flattens a rejection into `new Error(
    // data.error)` and only re-attaches `.response` for 403 — which would throw
    // away the 422 payload's `details`, i.e. the parser explaining WHY the file
    // was rejected. That reason is the whole point of the manual import, so
    // every status except 401 (which must still trigger the logout path) is
    // accepted here and turned into an error that carries the response.
    const response = await this.api.post(`/files/${fileId}/import-balance`, undefined, {
      validateStatus: (status) => status !== 401
    })
    if (response.status >= 400) {
      const data = (response.data ?? {}) as { error?: string; details?: string }
      const error = new Error(data.error || 'Balance import failed')
      ;(error as any).response = response
      throw error
    }
    return response.data
  }

  async deleteBalanceSnapshot(snapshotId: string) {
    const response = await this.api.delete(`/balance-snapshots/${snapshotId}`)
    return response.data
  }

  // ---- Company profile (ANAF / ONRC) ----

  /** 404 with `code: 'no_profile'` when nothing has been fetched yet. */
  async getCompanyProfile(clientId: string) {
    const response = await this.api.get(`/clients/${clientId}/company-profile`)
    return response.data as CompanyProfileResponse
  }

  /** Accountant/admin only. 502 with `code: 'anaf_unavailable'` when ANAF is down. */
  async refreshCompanyProfile(clientId: string) {
    const response = await this.api.post(`/clients/${clientId}/company-profile/refresh`)
    return response.data as CompanyProfileResponse
  }

  /**
   * Filed annual financials (ANAF public data). 403 with
   * `code: 'feature_locked'` when the client's package lacks
   * `stats.annual_public`.
   */
  async getAnnualStatistics(clientId: string, cui?: string) {
    const response = await this.api.get(`/clients/${clientId}/statistics/annual`, {
      params: cui ? { cui } : {}
    })
    return response.data as AnnualStatisticsResponse
  }

  async updateClient(clientId: string, data: any) {
    const response = await this.api.patch(`/clients/${clientId}`, data)
    return response.data
  }

  async deleteClient(clientId: string) {
    const response = await this.api.delete(`/clients/${clientId}`)
    return response.data
  }

  async getClientsNeedingAttention(params?: { assigned_only?: boolean }) {
    const response = await this.api.get('/clients/needs-attention', { params })
    return response.data as {
      clients: Array<{
        id: string
        societate: string
        reprezentant: string | null
        unread_messages: number
        pending_files: number
      }>
      total: number
    }
  }

  async getAuditLogs(params?: {
    page?: number
    actor_id?: string
    event_type?: string
    start_date?: string
    end_date?: string
    object_type?: string
    object_id?: string
    correlation_id?: string
    include_noise?: boolean
  }) {
    const response = await this.api.get('/audit/events', { params })
    return response.data
  }

  /**
   * `context` is only read for the 'formula' category, and the backend accepts
   * an identity whitelist from it (metric, cui, period, formula_version) —
   * never amounts. These messages leave the platform for Telegram/Discord.
   */
  async submitFeedback(category: string, message: string, context?: Record<string, unknown>) {
    const response = await this.api.post('/feedback', { category, message, context })
    return response.data
  }

  /**
   * Search messages across a conversation using the backend search API.
   * Unlike getMessages() which only loads a page of messages, this searches ALL messages.
   */
  async searchMessages(params: {
    conversation_id: string
    q?: string
    date_from?: string
    date_to?: string
    per_page?: number
  }): Promise<{
    results: Array<{
      id: string
      text: string
      created_at: string
      creation_sequence?: number
      sender_id: string
      sender_name: string
      conversation_id: string
    }>
    pagination: { total: number; page: number; pages: number; has_next: boolean }
  }> {
    const response = await this.api.get('/search/messages', { params })
    return response.data
  }

  // ---- TDec connector (read-only ANAF bridge) ----

  async getConnectorStatus() {
    const response = await this.api.get('/connector/status')
    return response.data as ConnectorStatus
  }

  async getConnectorCommands(limit?: number) {
    const response = await this.api.get('/connector/commands', { params: { limit } })
    return response.data as { commands: ConnectorCommand[]; total: number }
  }

  async getConnectorCommand(id: string) {
    const response = await this.api.get(`/connector/commands/${id}`)
    return response.data as ConnectorCommand
  }

  async createConnectorCommand(
    action: ConnectorAction,
    params: Record<string, any> = {},
    save?: ConnectorSaveMeta
  ) {
    // `save` is download-only and is validated server-side exactly as strictly
    // as `params`; it is omitted entirely rather than sent as null so the
    // backend's "no save requested" branch stays the default.
    const body: Record<string, any> = { action, params }
    if (save) body.save = save
    const response = await this.api.post('/connector/commands', body)
    return response.data as ConnectorCommand
  }

  /**
   * Documents already archived from TDec. Pass `uids` (the UIDs of the rows on
   * screen) to find out which of them are already filed — the endpoint caps the
   * set at 300, so callers must chunk longer lists themselves.
   */
  async getConnectorDocuments(filters?: {
    cif?: string
    category?: string
    client_id?: string
    period?: string
    uids?: string[] | string
    limit?: number
    offset?: number
  }) {
    const params: Record<string, any> = { ...filters }
    if (Array.isArray(params.uids)) params.uids = params.uids.join(',')
    const response = await this.api.get('/connector/documents', { params })
    return response.data as { documents: ConnectorDocument[]; total: number }
  }

  /**
   * Start a full archive sync (202) — it only ENQUEUES work; the agent executes
   * it over the following polls.
   *
   * Opts out of the shared interceptor for the same reason `importBalance()`
   * does: a 409 carries the run that is already in progress, and the generic
   * branch would flatten the body to its `error` string and throw the run away.
   */
  async startConnectorSync() {
    const response = await this.api.post('/connector/sync', undefined, {
      validateStatus: (status) => status !== 401
    })
    if (response.status >= 400) {
      const data = (response.data ?? {}) as { error?: string; run?: ConnectorSyncRun }
      const error = new Error(data.error || 'Sync could not be started')
      ;(error as any).response = response
      throw error
    }
    return response.data as ConnectorSyncRun
  }

  // ---- Subscription packages (per-client, offline-paid, admin-managed) ----
  //
  // Every route lives under /api/subscriptions and is JWT-authenticated. While
  // the platform-wide enforcement switch is off the packages still exist and
  // are still assignable — they simply gate nothing, which is what makes the
  // rollout invisible to clients.

  async getSubscriptionPackages(params?: { include_inactive?: boolean }) {
    const response = await this.api.get('/subscriptions/packages', {
      params: params?.include_inactive ? { include_inactive: 1 } : undefined
    })
    // Admins additionally get `feature_keys` — the bare key list. The grouped
    // catalog the package editor needs comes from `getSubscriptionFeatures()`.
    return response.data as {
      packages: SubscriptionPackage[]
      feature_keys?: string[]
    }
  }

  async createSubscriptionPackage(data: {
    code: string
    name: string
    description?: string | null
    price_ron: number
    tier: number
    is_default?: boolean
    is_active?: boolean
    features: string[]
  }) {
    const response = await this.api.post('/subscriptions/packages', data)
    return response.data as { package: SubscriptionPackage }
  }

  /** `code` is immutable server-side; it is never sent from the edit form. */
  async updateSubscriptionPackage(
    packageId: string,
    data: {
      name?: string
      description?: string | null
      price_ron?: number
      tier?: number
      is_default?: boolean
      is_active?: boolean
      features?: string[]
    }
  ) {
    const response = await this.api.patch(`/subscriptions/packages/${packageId}`, data)
    return response.data as { package: SubscriptionPackage }
  }

  async getSubscriptionFeatures() {
    const response = await this.api.get('/subscriptions/features')
    const data = (response.data ?? {}) as {
      features?: SubscriptionFeatureKey[]
      feature_keys?: SubscriptionFeatureKey[]
    }
    return { feature_keys: data.features ?? data.feature_keys ?? [] }
  }

  async getSubscriptionSettings() {
    const response = await this.api.get('/subscriptions/settings')
    return response.data as { subscriptions_enforced: boolean }
  }

  async updateSubscriptionSettings(data: { subscriptions_enforced: boolean }) {
    const response = await this.api.patch('/subscriptions/settings', data)
    return response.data as { subscriptions_enforced: boolean }
  }

  /**
   * The signed-in client's own subscription. 403s with `not_enforced` while the
   * switch is off — the same state in which no UI offers this call.
   */
  async getMySubscription() {
    const response = await this.api.get('/subscriptions/me')
    return response.data as {
      subscription?: ClientSubscription
      package?: SubscriptionPackage | null
      since?: string | null
      pending?: PendingSubscriptionChange | null
      features?: string[]
      packages?: SubscriptionPackage[]
    }
  }

  /**
   * Self-service change. The server decides WHEN from the tiers: a higher tier
   * applies immediately, a lower one on the 1st of next month.
   */
  async changeMySubscription(packageId: string) {
    const response = await this.api.post('/subscriptions/me/change', { package_id: packageId })
    return response.data
  }

  async cancelMyPendingSubscription() {
    const response = await this.api.delete('/subscriptions/me/pending')
    return response.data
  }

  async getSubscriptionClients(params?: {
    page?: number
    per_page?: number
    firm_id?: string
    package_id?: string
    search?: string
    pending_only?: boolean
  }) {
    const query: Record<string, any> = {}
    if (params?.page) query.page = params.page
    if (params?.per_page) query.per_page = params.per_page
    if (params?.firm_id) query.firm_id = params.firm_id
    if (params?.package_id) query.package_id = params.package_id
    if (params?.search) query.search = params.search
    if (params?.pending_only) query.pending_only = 1
    const response = await this.api.get('/subscriptions/clients', { params: query })
    return response.data as SubscriptionClientsResponse
  }

  /** Admin-side change; `when` is explicit here rather than derived from tiers. */
  async changeClientSubscription(
    clientId: string,
    packageId: string,
    when: 'now' | 'next_month' = 'now'
  ) {
    const response = await this.api.post(`/subscriptions/clients/${clientId}/change`, {
      package_id: packageId,
      when
    })
    return response.data
  }

  async cancelClientPendingSubscription(clientId: string) {
    const response = await this.api.delete(`/subscriptions/clients/${clientId}/pending`)
    return response.data
  }

  /**
   * The change log, read from the audit trail rather than the periods table, so
   * a cancelled downgrade stays visible instead of vanishing with its row.
   */
  async getSubscriptionHistory(clientId: string) {
    const response = await this.api.get(`/subscriptions/clients/${clientId}/history`)
    const data = (response.data ?? {}) as {
      history?: SubscriptionHistoryEvent[]
      events?: SubscriptionHistoryEvent[]
    }
    return { events: data.history ?? data.events ?? [] }
  }

  async getSubscriptionReport(params: {
    year: number
    month: number
    firm_id?: string
    package_id?: string
  }) {
    const response = await this.api.get('/subscriptions/report', { params })
    return response.data as SubscriptionReport
  }

  /** Same blob-download pattern as `downloadInvoiceDocument`. */
  async downloadSubscriptionReportCsv(params: {
    year: number
    month: number
    firm_id?: string
    package_id?: string
  }) {
    const response = await this.api.get('/subscriptions/report', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    })
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // The server names the file (`abonamente_YYYY-MM.csv`); honour it so the
    // download matches what the report is filed as everywhere else. The local
    // guess only stands in when the header is missing or unparseable.
    link.download =
      filenameFromContentDisposition(response.headers?.['content-disposition']) ||
      `abonamente-${params.year}-${String(params.month).padStart(2, '0')}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  // Note: Export endpoint not implemented in backend
  // async exportAuditLogs(params?: any) {
  //   const response = await this.api.get('/audit/export', {
  //     params,
  //     responseType: 'blob'
  //   })
  //   return response.data
  // }
}

export default new ApiService()
